// App startup, moved out of main.tsx so it can be tested (src/__tests__/
// bootstrap.test.tsx and bootstrap.i18n-on.test.tsx).
//
// The order is load-bearing:
//   1. the three synchronous calls, in exactly the order main.tsx always ran
//      them;
//   2. `await initLanguage()`, which picks and loads the interface language
//      (skipped with VITE_I18N off, so the flag-off render stays synchronous);
//   3. only then createRoot().render(), so the first painted frame is already
//      in the chosen language, with no English frame first.
// On a route with prerendered HTML the current page's chunk is preloaded
// alongside step 2 (src/routePreload.ts), capped at 3s, so the first commit
// is the page itself and the static markup is never swapped for a spinner.
// initLanguage() never rejects, so a failed catalog load still renders
// (in English).
import { StrictMode, createElement } from 'react'
import { createRoot, type Root } from 'react-dom/client'
import App from './App'
import { initGA4 } from './lib/ga4'
import { loadStores } from './api/stores'
import { primeStoreArm, recordStoreArmExposure } from './utils/cardVendor'
import { installTranslateGuard } from './lib/translateGuard'
import { initLanguage } from './i18n'
import { i18nEnabled } from './i18n/flag'
import { preloadRoute } from './routePreload'
import { captureHandoff } from './lib/prerenderHandoff'
import { androidAppBillingCapable, isAndroidApp } from './lib/androidApp'
import { initOfflineCards } from './lib/offlineCards/client'

// Resolves to the React root (tests unmount it; main.tsx ignores it).
export async function bootstrap(root: HTMLElement): Promise<Root> {
  // Before the first render: browser page translation moves React's text
  // nodes and would otherwise crash the route on the next re-render.
  installTranslateGuard()

  // Must run BEFORE the first render: GA4PageViews mounts as a child of App
  // and fires its page_view effect before App's own effect would, so
  // initGA4() has to already be initialised by the time that happens.
  initGA4()

  // Which card stores the buy links may use. Fire-and-forget, once per page
  // load: until it answers (and on any failure) the picker offers the two
  // stores that predate /stores. Kicked off here rather than from the hooks
  // so component tests and the prerender never issue it.
  // With VITE_STORE_DEFAULT_SPLIT on, record this session's split exposure
  // once the effective store is known (a no-op when the flag is off).
  void Promise.resolve(loadStores()).then(() => recordStoreArmExposure())

  // Default-store split: resolve the arm before the first render so no
  // render mints the analytics client id. Synchronous, no-op with it off.
  // Placed after the three pinned startup calls so their order is unchanged.
  primeStoreArm()

  // Read the Android-app signal while the launch URL still carries
  // `?source=twa`: the first client-side navigation drops it, and this call
  // remembers the answer for the tab (src/lib/androidApp.ts). The Play
  // billing marker (`?billing=1`) rides on the same launch URL, so it is read
  // now too.
  isAndroidApp()
  androidAppBillingCapable()

  // The page chunk for a prerendered route, fetched in parallel with the
  // language catalog. null on every other route, which keeps their first
  // render exactly as it was (synchronous with the flag off).
  const pagePreload = preloadRoute(window.location.pathname)

  // Flag off, initLanguage() would return 'en' at once anyway; skipping the
  // await keeps the first render synchronous, exactly as before the flag.
  if (i18nEnabled()) await initLanguage()
  // Never rejects and never outlasts its cap (PRELOAD_CAP_MS).
  if (pagePreload) await pagePreload

  // The static page's fields have been live while we waited. Note them now,
  // as late as possible; the components read their values back when they
  // mount (src/lib/prerenderHandoff.ts). A no-op on a page with no
  // prerendered fields.
  captureHandoff(root, document.activeElement)

  const reactRoot = createRoot(root)
  reactRoot.render(createElement(StrictMode, null, createElement(App)))
  // Offline card search (W5): reads what is installed and runs the daily
  // manifest check, after the first render. A no-op outside the installed
  // app (a normal browser tab never touches it).
  void initOfflineCards()
  return reactRoot
}
